<script setup lang="ts">
import { useAppStore } from '~/store/useAppStore'
import { useConfigStore } from '~/store/useConfigStore'
import type { ScheduleEvent } from '~/types'

defineOptions({
  name: 'ScheduleEventsTicker',
})

const configStore = useConfigStore()
const appStore = useAppStore()
const { defaultSeasonId, defaultWeekId } = storeToRefs(configStore)
const { isTickerOpened } = storeToRefs(appStore)

const weekId = ref<Maybe<number>>(defaultWeekId.value)

const params = computed<ScheduleEvent.SearchRequest>(() => ({
  include: [
    'homeTeam.logo',
    'awayTeam.logo',
    'scheduleEventLinks',
    'scheduleEventBroadcasts',
  ],
  filter: {
    schedule_week_id: weekId.value,
  },
  sort: 'datetime',
  per_page: 4,
}))

const { fetchData, isLoading } = useLoadScheduleEvents(params)
const { data: tickerItems } = await useAsyncData(
  `ticker-items-${weekId.value}`,
  () => fetchData(),
  {
    watch: [params],
  },
)
const { items: scheduleEvents } = useScheduleEvents(tickerItems)
</script>

<template>
  <section
    class="schedule-events-ticker"
    :class="{ 'schedule-events-ticker--opened': isTickerOpened }"
    role="navigation"
  >
    <Wrapper class="schedule-events-ticker__wrapper">
      <div class="schedule-events-ticker__dropdown-wrapper">
        <UIWeeksDropdown
          v-if="defaultSeasonId"
          v-model="weekId"
          :season-id="defaultSeasonId"
          class="schedule-events-ticker__dropdown"
        />
      </div>

      <ScheduleEventsTickerList
        :schedule-events
        :is-loading
        class="schedule-events-ticker__ticker"
      />

      <div class="schedule-events-ticker__buttons">
        <UIButton
          href="/scores"
          variant="outlined-dark"
          size="sm"
          class="schedule-events-ticker__button"
        >
          Full Scoreboard
        </UIButton>
      </div>
    </Wrapper>
  </section>
</template>

<style lang="scss" scoped>
.schedule-events-ticker {
  @apply overflow-hidden bg-white fixed top-0 -right-[100%] z-[4] w-full h-auto min-h-screen transition-all duration-300;
  @apply md:-right-[400px] md:w-[400px];
  @apply xl:h-[75px] xl:static xl:z-[unset] xl:w-full xl:min-h-[unset];

  &--opened {
    @apply right-0;
  }

  &__wrapper {
    @apply flex flex-col items-center justify-between h-full pt-[90px];
    @apply max-xl:px-0;
    @apply xl:flex-row xl:pt-0;
  }

  &__dropdown {
    @apply px-[14px];
    @apply xl:px-0;
  }

  &__dropdown:deep(.dropdown__select) {
    @apply min-w-[unset] w-full;
    @apply xl:w-[125px];
  }

  &__dropdown-wrapper,
  &__buttons {
    @apply w-full;
    @apply xl:w-[150px];
  }

  &__buttons {
    @apply hidden items-center justify-end pl-[10px];
    @apply xl:flex;
  }
}
</style>
